
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { post } from "../../services/api";

const ImportStudentDataModal = ({ onClose, onSuccess }) => {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
    trigger,
  } = useForm({
    defaultValues: {
      file: null,
    },
  });

  const [fileName, setFileName] = useState("");
  
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1); // 1: Download, 2: Upload

  const templateFileUrl = "https://api.inspirertechnologies.com/externalImages/Organization/DataExcelTemplate.xlsx";

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setFileName(file.name);
      setValue("file", file);
      trigger("file");
    } else {
      setFileName("");
      setValue("file", null);
      trigger("file");
    }
  };

  const handleDownloadTemplate = () => {
    window.open(templateFileUrl, "_blank");
  };

  const onSubmit = async (data) => {
    if (!data.file) {
      toast.error("Please select a file to upload.");
      return;
    }

    const createdBy = localStorage.getItem("userId");
    const centerId = localStorage.getItem("centerId");

    if (!createdBy || !centerId) {
      toast.error("User ID or Center ID missing. Please login again.");
      return;
    }

    setLoading(true);
    const formData = new FormData();
    formData.append("file", data.file);
    formData.append("createdBy", createdBy);
    formData.append("centerId", centerId);

    try {
      const response = await post("InspRegistration/import-excel", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (response && (response.success || response.isSuccess)) {
        toast.success(response.message || "Import completed successfully");
        if (onSuccess) onSuccess();
        onClose();
      } else {
        toast.error(response?.message || "Failed to import data.");
      }
    } catch (error) {
      console.error("Import error:", error);
      toast.error("An error occurred during import.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div
        className="modal fade show"
        tabIndex="-1"
        style={{ display: "block" }}
      >
        <div className="modal-dialog modal-dialog-centered" role="document">
          <div className="modal-content shadow-lg border-0">
            <div className="modal-header bg-light">
              <h5 className="mb-0 fw-bold text-primary">
                {step === 1 ? "Step 1: Download Template" : "Step 2: Upload Data"}
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={onClose}
              ></button>
            </div>
            <div className="modal-body p-4">
              {step === 1 ? (
                <div className="text-center">
                  <div className="mb-4">
                    <i className="fas fa-file-excel text-success display-4 mb-3"></i>
                    <h6 className="fw-bold">Download Excel Template</h6>
                    <p className="text-muted small">
                      Please download the template file and fill it with the required student data. 
                      Ensure all fields follow the format specified in the template.
                    </p>
                  </div>
                  <button
                    type="button"
                    className="btn btn-outline-success btn-lg w-100 mb-3 d-flex align-items-center justify-content-center gap-2"
                    onClick={handleDownloadTemplate}
                  >
                    <i className="fas fa-download"></i> Download Template
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary w-100"
                    onClick={() => setStep(2)}
                  >
                    Next: Upload Data <i className="fas fa-arrow-right ms-2"></i>
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit(onSubmit)}>
                  <div className="mb-4">
                    <label htmlFor="importFile" className="form-label fw-bold">
                      Upload Completed Excel File <span className="text-danger">*</span>
                    </label>
                    <div className="input-group">
                      <span className="input-group-text bg-white">
                        <i className="fas fa-upload text-primary"></i>
                      </span>
                      <input
                        type="file"
                        className="form-control"
                        id="importFile"
                        accept=".xlsx, .xls"
                        onChange={handleFileChange}
                        disabled={loading}
                      />
                    </div>
                    {errors.file && (
                      <div className="text-danger small mt-1">Please select a file.</div>
                    )}
                    <input
                      type="hidden"
                      {...register("file", { required: "File is required" })}
                    />
                  </div>
                  
                  {fileName && (
                    <div className="alert alert-success d-flex align-items-center py-2 mb-4">
                      <i className="fas fa-check-circle me-2"></i>
                      <div className="small text-truncate">Selected: <strong>{fileName}</strong></div>
                    </div>
                  )}

                  <div className="alert alert-info py-2 fs-9 mb-4">
                    <i className="fas fa-info-circle me-2"></i>
                    Only .xlsx and .xls formats are supported.
                  </div>

                  <div className="d-flex justify-content-between gap-2 mt-4">
                    <button
                      type="button"
                      className="btn btn-outline-secondary px-4"
                      onClick={() => setStep(1)}
                      disabled={loading}
                    >
                      <i className="fas fa-arrow-left me-2"></i> Back
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary px-4"
                      disabled={!fileName || isSubmitting || loading}
                    >
                      {loading ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm me-2"
                            role="status"
                            aria-hidden="true"
                          ></span>
                          Importing...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-cloud-upload-alt me-2"></i> Import Data
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show"></div>
    </>
  );
};

export default ImportStudentDataModal;

